<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import AccessibleModal from '../shared/AccessibleModal.svelte';
    import { settingsStore } from '../../stores/settingsStore.js';
    import { clerkService } from '../../services/clerkService.js';

    const dispatch = createEventDispatcher();

    // Get backend URL
    const backendUrl = import.meta.env?.VITE_API_URL ||
                      ((window.location.hostname.includes('vercel.app') ||
                        window.location.hostname === 'patcraft-demo.fearn.ai' ||
                        window.location.hostname === 'patcraft-pilot.fearn.ai') ?
                       'https://patcraft-production.up.railway.app' : 'http://localhost:5001');

    // Debounce timer for auto-save
    let autoSaveTimer = null;

    export let show = false;

    let customerFile = null;
    let drafterFile = null;
    let customerFileName = '';
    let drafterFileName = '';

    // Custom text styles
    let style1 = {
        delimiter: '{{ }}',
        color: '#859BFF',
        bold: false,
        italic: false,
        underline: false
    };

    let style2 = {
        delimiter: '\\\\ \\\\',
        color: '#D4548A',
        bold: false,
        italic: false,
        underline: false
    };

    // Load settings when modal opens
    $: if (show) {
        loadSettings();
    }

    function loadSettings() {
        const settings = $settingsStore.personalization;
        if (settings && settings.customStyles) {
            style1 = { ...style1, ...settings.customStyles.style1 };
            style2 = { ...style2, ...settings.customStyles.style2 };
        }
    }

    // Save personalization styles to backend
    async function saveStylesToBackend() {
        // Save to localStorage immediately
        settingsStore.updateSetting('personalization.customStyles.style1', style1);
        settingsStore.updateSetting('personalization.customStyles.style2', style2);

        // Get the updated settings from the store
        let updatedSettings;
        const unsubscribe = settingsStore.subscribe(value => {
            updatedSettings = value;
        });
        unsubscribe();

        // Save to backend
        try {
            // Get authentication token
            const token = await clerkService.getToken();

            const url = `${backendUrl}/api/user/profile/settings`;

            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                credentials: 'include',
                body: JSON.stringify({
                    settings: updatedSettings
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[PersonalizationModal] Failed to save styles to backend:', {
                    status: response.status,
                    statusText: response.statusText,
                    error: errorText
                });
            }
        } catch (error) {
            console.error('[PersonalizationModal] Error saving styles:', error);
        }
    }

    function handleCustomerFileSelect(event) {
        const file = event.target.files?.[0];
        if (file) {
            customerFile = file;
            customerFileName = file.name;
        }
    }

    function handleDrafterFileSelect(event) {
        const file = event.target.files?.[0];
        if (file) {
            drafterFile = file;
            drafterFileName = file.name;
        }
    }

    async function handleClose() {
        // Save current personalization settings before closing
        await saveStylesToBackend();

        // Reset file upload state only
        customerFile = null;
        drafterFile = null;
        customerFileName = '';
        drafterFileName = '';

        dispatch('close');
    }

    async function handleSubmit() {
        // Dispatch all personalization data to parent (for file upload handling)
        dispatch('submit', {
            customerFile,
            drafterFile,
            customStyles: {
                style1,
                style2
            }
        });
        handleClose();
    }

    // Always allow submitting to save custom styles
    $: canSubmit = true;
</script>

<AccessibleModal
    show={show}
    title="Personalization"
    on:close={handleClose}
    size="medium"
>
    <div class="personalization-content">
        <!-- Action buttons at top -->
        <div class="modal-actions-top">
            <button class="btn btn-secondary" on:click={handleClose}>
                Cancel
            </button>
            <button
                class="modal-button modal-button-accent"
                on:click={handleSubmit}
                disabled={!canSubmit}
            >
                Save
            </button>
        </div>

        <!-- Scrollable content area -->
        <div class="scrollable-content">
            <!-- Custom Text Styles Section -->
            <div class="section">
                <h3 class="section-label">Custom Text Styles</h3>

                <!-- Style 1: {{ }} -->
                <div class="style-config">
                    <div class="style-header">
                        <span class="delimiter-display">{'{{ text }}'}</span>
                    </div>
                    <div class="style-controls">
                        <div class="control-group">
                            <label for="style1-color" class="control-label">Color</label>
                            <input
                                id="style1-color"
                                type="color"
                                bind:value={style1.color}
                                class="color-picker"
                            />
                        </div>
                        <div class="checkbox-group">
                            <label class="checkbox-label">
                                <input type="checkbox" bind:checked={style1.bold} />
                                <span>Bold</span>
                            </label>
                            <label class="checkbox-label">
                                <input type="checkbox" bind:checked={style1.italic} />
                                <span>Italic</span>
                            </label>
                            <label class="checkbox-label">
                                <input type="checkbox" bind:checked={style1.underline} />
                                <span>Underline</span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Style 2: \\ \\ -->
                <div class="style-config">
                    <div class="style-header">
                        <span class="delimiter-display">\\ text \\</span>
                    </div>
                    <div class="style-controls">
                        <div class="control-group">
                            <label for="style2-color" class="control-label">Color</label>
                            <input
                                id="style2-color"
                                type="color"
                                bind:value={style2.color}
                                class="color-picker"
                            />
                        </div>
                        <div class="checkbox-group">
                            <label class="checkbox-label">
                                <input type="checkbox" bind:checked={style2.bold} />
                                <span>Bold</span>
                            </label>
                            <label class="checkbox-label">
                                <input type="checkbox" bind:checked={style2.italic} />
                                <span>Italic</span>
                            </label>
                            <label class="checkbox-label">
                                <input type="checkbox" bind:checked={style2.underline} />
                                <span>Underline</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <!-- File Upload Section -->
            <div class="section">
                <h3 class="section-label">Example Files</h3>
                <div class="file-upload-section">
                    <div class="upload-group">
                        <label for="customer-file">Customer Example</label>
                        <input
                            id="customer-file"
                            type="file"
                            accept=".pdf,.doc,.docx,.txt"
                            on:change={handleCustomerFileSelect}
                            class="file-input"
                        />
                        {#if customerFileName}
                            <div class="file-name">
                                <i class="fas fa-file"></i>
                                <span>{customerFileName}</span>
                            </div>
                        {/if}
                    </div>

                    <div class="upload-group">
                        <label for="drafter-file">Drafter Example</label>
                        <input
                            id="drafter-file"
                            type="file"
                            accept=".pdf,.doc,.docx,.txt"
                            on:change={handleDrafterFileSelect}
                            class="file-input"
                        />
                        {#if drafterFileName}
                            <div class="file-name">
                                <i class="fas fa-file"></i>
                                <span>{drafterFileName}</span>
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
</AccessibleModal>

<style>
.personalization-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
    padding: var(--spacing-4);
    max-height: 70vh;
}

.description {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
}

.modal-actions-top {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-3);
    padding-bottom: var(--spacing-3);
    border-bottom: 1px solid var(--border-color-default);
}

.scrollable-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-6);
    overflow-y: auto;
    padding-right: var(--spacing-2);
}

/* Always show scrollbar */
.scrollable-content::-webkit-scrollbar {
    width: 8px;
    display: block;
}

.scrollable-content::-webkit-scrollbar-track {
    background: var(--color-bg-primary);
    border-radius: var(--border-radius-md);
}

.scrollable-content::-webkit-scrollbar-thumb {
    background: var(--color-text-tertiary);
    border-radius: var(--border-radius-md);
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-secondary);
}

.file-upload-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
}

.upload-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);
}

.upload-group label {
    font-size: var(--font-size-sm);
    font-weight: 400;
    color: var(--color-text-primary);
}

.file-input {
    padding: var(--spacing-2);
    font-size: var(--font-size-sm);
    background-color: var(--color-bg-primary);
    border: 1px solid var(--border-color-default);
    border-radius: var(--border-radius-md);
    cursor: pointer;
}

.file-input:hover {
    border-color: var(--color-primary-500);
}

.file-name {
    display: flex;
    align-items: center;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    background-color: var(--color-surface-secondary);
    border-radius: var(--border-radius-md);
    gap: var(--spacing-2);
}

.file-name i {
    color: var(--color-primary-500);
}

.section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
}

.section-label {
    font-size: var(--font-size-base);
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
}

.style-config {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    padding: var(--spacing-3);
    background-color: var(--color-bg-main);
    border: 1px solid var(--border-color-default);
    border-radius: var(--border-radius-md);
}

.style-header {
    display: flex;
    align-items: center;
}

.delimiter-display {
    font-family: monospace;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-text-primary);
    padding: var(--spacing-1) var(--spacing-2);
    background-color: var(--color-bg-primary);
    border-radius: var(--border-radius-sm);
}

.style-controls {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
}

.control-group {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
}

.control-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    min-width: 50px;
}

.color-picker {
    width: 60px;
    height: 32px;
    border: 1px solid var(--border-color-default);
    border-radius: var(--border-radius-md);
    cursor: pointer;
}

.checkbox-group {
    display: flex;
    gap: var(--spacing-4);
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    cursor: pointer;
    user-select: none;
}

.checkbox-label input[type="checkbox"] {
    cursor: pointer;
}

.checkbox-label span {
    line-height: 1;
}

.btn {
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    font-weight: 600;
    border: none;
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
}

.btn-secondary {
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
}

.btn-secondary:hover {
    background-color: var(--color-btn-default-hover);
}
</style>
